/**
 * ===== SERVICE D'ACCESSIBILITÉ AVANCÉE =====
 * Améliore l'accessibilité pour les utilisateurs avec des besoins spéciaux;
 */
export class AccessibilityService {
  private static instance: AccessibilityService;
  private isScreenReaderActive: boolean = false;
  private isHighContrastMode: boolean = false;
  private isReducedMotion: boolean = false;
  private currentFocusElement: HTMLElement | null = null;
  private focusHistory: HTMLElement[] = [];
  // Stocker les références des event listeners pour pouvoir les retirer;
  private boundHandlers: {
    focusIn: (e: FocusEvent) => void;
    focusOut: (e: FocusEvent) => void;
    keyDown: (e: KeyboardEvent) => void;
    contrastChange: (e: MediaQueryListEvent) => void;
    motionChange: (e: MediaQueryListEvent) => void;
  };
  private constructor() {
    // Créer les références des handlers une seule fois;
    this.boundHandlers = {
      focusIn: this.handleFocusIn.bind(this),
      focusOut: this.handleFocusOut.bind(this),
      keyDown: this.handleKeyboardNavigation.bind(this),
      contrastChange: (e: MediaQueryListEvent) => {
        this.isHighContrastMode = e.matches;
        this.updateHighContrastMode();
      },
      motionChange: (e: MediaQueryListEvent) => {
        this.isReducedMotion = e.matches;
        this.updateReducedMotion();
      },
    };
    this.detectAccessibilityPreferences();
    this.setupEventListeners();
    this.initializeAccessibilityFeatures();
  }
  public static getInstance(): AccessibilityService {
    if (!AccessibilityService.instance) {
      AccessibilityService.instance = new AccessibilityService();
    }
    return AccessibilityService.instance;
  }

  /**
   * Initialise le service d'accessibilité
   */
  public initialize(): void {
    // Le service est déjà initialisé dans le constructeur
    // Cette méthode est fournie pour la compatibilité avec l'interface
  }

  private detectAccessibilityPreferences(): void {
    // Détecter les préférences d'accessibilité
    this.isHighContrastMode = window.matchMedia('(prefers-contrast: high)').matches;
    this.isReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Détecter les lecteurs d'écran;
    this.isScreenReaderActive = this.detectScreenReader();
  }
  private detectScreenReader(): boolean {
    // Détection basique des lecteurs d'écran;
    // Ne pas activer automatiquement la synthèse vocale - seulement si un lecteur d'écran est vraiment détecté
    // Vérifier les préférences système pour les lecteurs d'écran
    const _prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Détecter uniquement les vrais lecteurs d'écran (pas juste la disponibilité de speechSynthesis)
    const _hasScreenReader =
      navigator.userAgent.includes('NVDA') ||
      navigator.userAgent.includes('JAWS') ||
      navigator.userAgent.includes('VoiceOver') ||
      // Vérifier si l'utilisateur a activé un lecteur d'écran via les préférences d'accessibilité
      (window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
        document.documentElement.getAttribute('data-screen-reader') === 'true');

    // Ne pas activer automatiquement - nécessite une activation explicite
    // Pour désactiver complètement, retourner false
    return false; // Désactivé par défaut pour éviter les annonces vocales non désirées
  }
  private setupEventListeners(): void {
    // Écouter les changements de préférences;
    window
      .matchMedia('(prefers-contrast: high)')
      .addEventListener('change', this.boundHandlers.contrastChange);
    window
      .matchMedia('(prefers-reduced-motion: reduce)')
      .addEventListener('change', this.boundHandlers.motionChange);
    // Gestion du focus;
    document.addEventListener('focusin', this.boundHandlers.focusIn);
    document.addEventListener('focusout', this.boundHandlers.focusOut);
    // Navigation au clavier;
    document.addEventListener('keydown', this.boundHandlers.keyDown);
  }
  private initializeAccessibilityFeatures(): void {
    this.setupARIALabels();
    this.setupKeyboardNavigation();
    this.setupScreenReaderSupport();
    this.setupHighContrastMode();
    this.setupReducedMotion();
  }
  private setupARIALabels(): void {
    // Ajouter des labels ARIA pour les éléments interactifs;
    // Canvas elements;
    const canvas = document.querySelector('.canvas-container');
    if (canvas) {
      canvas.setAttribute('role', 'img');
      canvas.setAttribute(
        'aria-label',
        'Zone de dessin - Utilisez les outils pour créer votre design'
      );
      canvas.setAttribute('tabindex', '0');
    }
    // Color picker;
    const colorPickers = document.querySelectorAll('.color-picker');
    colorPickers.forEach((picker, index) => {
      picker.setAttribute('aria-label', `Sélecteur de couleur ${index + 1}`);
      picker.setAttribute('role', 'button');
      picker.setAttribute('aria-describedby', `color-picker-help-${index}`);
    });
    // Zoom controls;
    const zoomControls = document.querySelectorAll('.zoom-control');
    zoomControls.forEach((control, index) => {
      control.setAttribute('aria-label', `Contrôles de zoom ${index + 1}`);
      control.setAttribute('role', 'group');
    });
    // Tool buttons;
    const toolButtons = document.querySelectorAll('.tool-btn');
    toolButtons.forEach((button, index) => {
      const toolName = button.querySelector('span')?.textContent || `Outil ${index + 1}`;
      button.setAttribute('aria-label', toolName);
      button.setAttribute('role', 'button');
    });
  }
  private setupKeyboardNavigation(): void {
    // Navigation au clavier améliorée;
    const focusableElements = document.querySelectorAll(
      'button, input, select, textarea, [tabindex]:not([tabindex="-1"]), .tool-btn, .color-picker'
    );
    focusableElements.forEach((element, index) => {
      element.setAttribute('tabindex', index === 0 ? '0' : '-1');
    });
  }
  private setupScreenReaderSupport(): void {
    if (!this.isScreenReaderActive) return;
    // Ajouter des descriptions pour les zones de dessin;
    this.addCanvasDescriptions();
    // Améliorer la navigation par drag & drop;
    this.enhanceDragDropAccessibility();
    // Ajouter des alternatives textuelles;
    this.addTextAlternatives();
  }
  private addCanvasDescriptions(): void {
    const canvas = document.querySelector('.canvas-container');
    if (canvas) {
      const description = document.createElement('div');
      description.id = 'canvas-description';
      description.className = 'sr-only';
      description.textContent =
        'Zone de dessin interactive. Utilisez les outils de la barre latérale pour ajouter des éléments. Utilisez Tab pour naviguer entre les éléments.';
      canvas.appendChild(description);
    }
  }
  private enhanceDragDropAccessibility(): void {
    // Améliorer l'accessibilité du drag & drop;
    const draggableElements = document.querySelectorAll('[draggable="true"]');
    draggableElements.forEach(element => {
      element.setAttribute('aria-grabbed', 'false');
      element.addEventListener('dragstart', () => {
        element.setAttribute('aria-grabbed', 'true');
        this.announceToScreenReader('Élément sélectionné pour déplacement');
      });
      element.addEventListener('dragend', () => {
        element.setAttribute('aria-grabbed', 'false');
        this.announceToScreenReader('Élément déplacé');
      });
    });
  }
  private addTextAlternatives(): void {
    // Ajouter des alternatives textuelles pour les color pickers;
    const colorPickers = document.querySelectorAll('.color-picker');
    colorPickers.forEach((picker, index) => {
      const helpText = document.createElement('div');
      helpText.id = `color-picker-help-${index}`;
      helpText.className = 'sr-only';
      helpText.textContent =
        'Sélectionnez une couleur. Utilisez les flèches pour naviguer et Entrée pour sélectionner.';
      picker.parentNode?.insertBefore(helpText, picker.nextSibling);
    });
  }
  private setupHighContrastMode(): void {
    if (this.isHighContrastMode) {
      document.documentElement.setAttribute('data-high-contrast', 'true');
      this.updateHighContrastMode();
    }
  }
  private updateHighContrastMode(): void {
    if (this.isHighContrastMode) {
      document.documentElement.style.setProperty('--color-border', '#000000');
      document.documentElement.style.setProperty('--color-border-hover', '#000000');
      document.documentElement.style.setProperty('--color-text-primary', '#000000');
      document.documentElement.style.setProperty('--color-background-primary', '#ffffff');
    } else {
      document.documentElement.removeAttribute('data-high-contrast');
    }
  }
  private setupReducedMotion(): void {
    if (this.isReducedMotion) {
      document.documentElement.setAttribute('data-reduced-motion', 'true');
      this.updateReducedMotion();
    }
  }
  private updateReducedMotion(): void {
    if (this.isReducedMotion) {
      // Désactiver les animations;
      document.documentElement.style.setProperty('--transition-fast', '0.01ms');
      document.documentElement.style.setProperty('--transition-normal', '0.01ms');
      document.documentElement.style.setProperty('--transition-slow', '0.01ms');
      // Désactiver les animations CSS;
      const style = document.createElement('style');
      style.textContent = `
        *, *::before, *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
        }
      `;
      document.head.appendChild(style);
    } else {
      document.documentElement.removeAttribute('data-reduced-motion');
    }
  }
  private handleFocusIn(event: FocusEvent): void {
    const target = event.target as HTMLElement;
    this.currentFocusElement = target;
    this.focusHistory.push(target);
    // Limiter l'historique du focus;
    if (this.focusHistory.length > 10) {
      this.focusHistory.shift();
    }
    // Annoncer le focus aux lecteurs d'écran;
    this.announceFocus(target);
  }
  private handleFocusOut(_event: FocusEvent): void {
    // Nettoyer si nécessaire;
  }
  private handleKeyboardNavigation(event: KeyboardEvent): void {
    const { key, ctrlKey, altKey, shiftKey } = event;
    // Navigation par tabulation personnalisée;
    if (key === 'Tab' && !ctrlKey && !altKey) {
      this.handleTabNavigation(event, shiftKey);
    }
    // Raccourcis clavier d'accessibilité
    if (ctrlKey && altKey) {
      switch (key) {
        case 'h':
          event.preventDefault();
          this.toggleHighContrast();
          break;
        case 'm':
          event.preventDefault();
          this.toggleReducedMotion();
          break;
        case 's':
          event.preventDefault();
          this.announceCurrentState();
          break;
      }
    }
  }
  private handleTabNavigation(event: KeyboardEvent, isShift: boolean): void {
    const focusableElements = Array.from(
      document.querySelectorAll(
        'button, input, select, textarea, [tabindex]:not([tabindex="-1"]), .tool-btn, .color-picker'
      )
    ) as HTMLElement[];
    if (!this.currentFocusElement) return;
    const currentIndex = focusableElements.indexOf(this.currentFocusElement);
    let nextIndex: number;
    if (isShift) {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
    } else {
      nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
    }
    event.preventDefault();
    focusableElements[nextIndex]?.focus();
  }
  private announceFocus(element: HTMLElement): void {
    if (this.isScreenReaderActive) {
      const label =
        element.getAttribute('aria-label') ||
        element.textContent?.trim() ||
        element.getAttribute('title') ||
        'Élément interactif';
      this.announceToScreenReader(`Focus sur: ${label}`);
    }
  }
  private announceToScreenReader(message: string): void {
    if (this.isScreenReaderActive && 'speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(message);
      utterance.volume = 0.8;
      utterance.rate = 0.9;
      speechSynthesis.speak(utterance);
    }
  }
  private toggleHighContrast(): void {
    this.isHighContrastMode = !this.isHighContrastMode;
    this.updateHighContrastMode();
    this.announceToScreenReader(
      this.isHighContrastMode ? 'Mode contraste élevé activé' : 'Mode contraste élevé désactivé'
    );
  }
  private toggleReducedMotion(): void {
    this.isReducedMotion = !this.isReducedMotion;
    this.updateReducedMotion();
    this.announceToScreenReader(
      this.isReducedMotion ? 'Animations réduites activées' : 'Animations réduites désactivées'
    );
  }
  private announceCurrentState(): void {
    const _canvas = document.querySelector('.canvas-container');
    const tools = document.querySelectorAll('.tool-btn');
    const layers = document.querySelectorAll('.layer-item');
    const message = `État actuel: ${tools.length} outils disponibles, ${layers.length} calques, zone de dessin active`;
    this.announceToScreenReader(message);
  }
  // Méthodes publiques;
  public isScreenReaderEnabled(): boolean {
    return this.isScreenReaderActive;
  }
  /**
   * Active ou désactive la synthèse vocale
   * @param enabled true pour activer, false pour désactiver
   */
  public setScreenReaderEnabled(enabled: boolean): void {
    this.isScreenReaderActive = enabled;
  }
  public isHighContrastEnabled(): boolean {
    return this.isHighContrastMode;
  }
  public isReducedMotionEnabled(): boolean {
    return this.isReducedMotion;
  }
  public announceToUser(message: string): void {
    this.announceToScreenReader(message);
  }
  public setFocus(element: HTMLElement): void {
    element.focus();
    this.announceFocus(element);
  }
  public getFocusHistory(): HTMLElement[] {
    return [...this.focusHistory];
  }
  public createAccessibleButton(
    text: string,
    onClick: () => void,
    options: {
      className?: string;
      ariaLabel?: string;
      disabled?: boolean;
    } = {}
  ): HTMLButtonElement {
    const button = document.createElement('button');
    button.textContent = text;
    button.className = options.className || 'btn btn-primary';
    button.setAttribute('aria-label', options.ariaLabel || text);
    button.disabled = options.disabled || false;
    button.addEventListener('click', onClick);
    return button;
  }
  public createAccessibleInput(
    type: string,
    label: string,
    options: {
      placeholder?: string;
      required?: boolean;
      value?: string;
    } = {}
  ): { input: HTMLInputElement; label: HTMLLabelElement; container: HTMLDivElement } {
    const container = document.createElement('div');
    container.className = 'input-group';
    const labelElement = document.createElement('label');
    labelElement.textContent = label;
    labelElement.className = 'input-label';
    const input = document.createElement('input');
    input.type = type;
    input.className = 'input';
    input.placeholder = options.placeholder || '';
    input.required = options.required || false;
    input.value = options.value || '';
    const id = `input-${Math.random().toString(36).substr(2, 9)}`;
    input.id = id;
    labelElement.setAttribute('for', id);
    container.appendChild(labelElement);
    container.appendChild(input);
    return { input, label: labelElement, container };
  }
  // Méthode pour nettoyer les event listeners;
  public destroy(): void {
    // Retirer tous les event listeners;
    window
      .matchMedia('(prefers-contrast: high)')
      .removeEventListener('change', this.boundHandlers.contrastChange);
    window
      .matchMedia('(prefers-reduced-motion: reduce)')
      .removeEventListener('change', this.boundHandlers.motionChange);
    document.removeEventListener('focusin', this.boundHandlers.focusIn);
    document.removeEventListener('focusout', this.boundHandlers.focusOut);
    document.removeEventListener('keydown', this.boundHandlers.keyDown);
    // Nettoyer les références;
    this.focusHistory = [];
    this.currentFocusElement = null;
  }
}
// CSS pour l'accessibilité
export const accessibilityCSS = `
/* === CLASSES D'ACCESSIBILITÉ === */
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.sr-only-focusable:focus {
  position: static;
  width: auto;
  height: auto;
  padding: inherit;
  margin: inherit;
  overflow: visible;
  clip: auto;
  white-space: normal;
}
/* === FOCUS VISIBLE === */
.focus-visible {
  outline: 2px solid var(--color-border-focus,);
  outline-offset: 2px;
}
.focus-visible-inset {
  outline: 2px solid var(--color-border-focus,);
  outline-offset: -2px;
}
/* === HIGH CONTRAST MODE === */
[data-high-contrast] {
  --color-border: #000000;
  --color-border-hover: #000000;
  --color-text-primary: #000000;
  --color-background-primary: #ffffff;
}
[data-high-contrast][data-theme='dark'] {
  --color-border: #ffffff;
  --color-border-hover: #ffffff;
  --color-text-primary: #ffffff;
  --color-background-primary: #000000;
}
/* === REDUCED MOTION === */
[data-reduced-motion] * {
  animation-duration: 0.01ms !important;
  animation-iteration-count: 1 !important;
  transition-duration: 0.01ms !important;
}
/* === SCREEN READER SUPPORT === */
.canvas-container[role="img"] {
  cursor: default;
}
.canvas-container[role="img"]:focus {
  outline: 2px solid var(--color-border-focus,);
  outline-offset: 2px;
}
/* === KEYBOARD NAVIGATION === */
.tool-btn:focus,
.color-picker:focus,
.zoom-control: focus {
  outline: 2px solid var(--color-border-focus,);
  outline-offset: 2px;
}
/* === DRAG AND DROP ACCESSIBILITY === */
[draggable="true"] {
  cursor: grab;
}
[draggable="true"]:active {
  cursor: grabbing;
}
[draggable="true"][aria-grabbed="true"] {
  opacity: 0.7;
  transform: scale(1.05,);
}
/* === INPUT GROUPS === */
.input-group {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.input-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary,);
}
.input-label: focus-within {
  color: var(--color-border-focus,);
}
/* === ACCESSIBILITY INDICATORS === */
.accessibility-indicator {
  position: fixed;
  top: 16px;
  left: 16px;
  background: var(--color-primary);
  color: var(--color-text-inverse);
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 1000;
  opacity: 0;
  transform: translateY(-10px,);
  transition: all 0.3s ease;
}
.accessibility-indicator.show {
  opacity: 1;
  transform: translateY(0,);
}
`;
